import { type AnyAction } from 'redux';
import { cloneDeep, get, head, includes, isEqual, isObject, mapValues } from 'lodash';
import queryString from 'query-string';
import { differenceInCalendarDays, isValid, startOfDay, subDays } from 'date-fns';
import { filterIds, mapViewAndGroupValues, reportMembers, reportTypes, reportViews } from '../../utils/filters';
import { setReportViewFilterFromDashboard } from '../../utils/reportButton';

import {
    SET_CARD_FILTER,
    SET_CARDS_FILTERS,
    SET_DATA_ROOM_MODE,
    SET_REPORT_FILTERS_FROM_DASHBOARD,
    SET_SELECTED_DASHBOARD_ITEMS,
    UPDATE_FILTER
} from '../actions/actionTypes/filters';
import { LOGOUT } from '../actions/actionTypes/user';
import { history } from '../history';
import {
    getKeywordsGranularityValues,
    getReportsGranularityValues,
    getDefaultGranularityValues,
    getSentimentGranularityValues
} from '../../components/Metrics/Strategies/Filters/granularityFilter';
import { DASHBOARD_CALENDAR_RANGE, REPORTING_CALENDAR_MAX_DAYS } from '../../utils/constants';
import { getTodayMidDay } from '../../utils/date';
import { dateFilterUrlParams } from '../middlewares/urlParamsSynchronizer';
import { type FiltersState } from '../types';

const initDates = () => {
    const todayMidDay = getTodayMidDay();

    return {
        from: subDays(todayMidDay, DASHBOARD_CALENDAR_RANGE),
        to: todayMidDay
    };
};

const shouldUpdateScorecardGranularity = (prevDates: any, nextDates: any) => {
    const prevItems = getDefaultGranularityValues(prevDates);
    const nextItems = getDefaultGranularityValues(nextDates);
    return !isEqual(prevItems, nextItems);
};

const shouldUpdateReportGranularity = (prevDates: any, nextDates: any) => {
    const prevItems = getReportsGranularityValues(prevDates);
    const nextItems = getReportsGranularityValues(nextDates);
    return !isEqual(prevItems, nextItems);
};

const shouldUpdateKeywordGranularity = (prevDates: any, nextDates: any) => {
    const prevItems = getKeywordsGranularityValues(prevDates);
    const nextItems = getKeywordsGranularityValues(nextDates);
    return !isEqual(prevItems, nextItems);
};

const shouldUpdateSentimentGranularity = (prevDates: any, nextDates: any) => {
    const prevItems = getSentimentGranularityValues(prevDates);
    const nextItems = getSentimentGranularityValues(nextDates);
    return !isEqual(prevItems, nextItems);
};

const defaultViewTypeFilter = { value: '', subvalues: [] };
export const defaultState: FiltersState = {
    [filterIds.datesFilter]: initDates(),
    [filterIds.salesDatesFilter]: { from: null, to: null },
    [filterIds.salesTeamsFilter]: [],
    [filterIds.teamFilter]: undefined,
    [filterIds.reportTypeFilter]: reportTypes.keywordGuidance,
    [filterIds.reportViewFilter]: defaultViewTypeFilter,
    [filterIds.agentsCommonDashboardFilter]: reportMembers.all,
    [filterIds.reportAgentsFilter]: reportMembers.all,
    [filterIds.reportGranularityFilter]: head(getReportsGranularityValues(initDates())),
    [filterIds.reportTabGroupFilter]: reportViews.all,
    cards: null,
    [filterIds.isDataRoomEnabled]: false,
    [filterIds.dashBoardSelections]: { itemType: null, itemsList: [] }
};

const getInitialState = (): FiltersState => {
    const queryParams = queryString.parse(history.location.search);
    const initialState = {
        ...cloneDeep(defaultState)
    };
    Object.keys(queryParams).forEach(key => {
        if (key === 'token') {
            return;
        }
        if (key === 'lang') {
            return;
        }
        if (key === 'templateId') {
            return;
        }
        if (key === 'redirect') {
            return;
        }
        try {
            const queryParamValue = queryParams[key];
            if (typeof queryParamValue !== 'string') {
                return;
            }
            const parsedValue = JSON.parse(queryParamValue);

            if (includes([dateFilterUrlParams.dateFrom, dateFilterUrlParams.dateTo], key)) {
                initialState[filterIds.datesFilter] = {
                    ...initialState[filterIds.datesFilter],
                    [key]: new Date(parsedValue)
                };
            } else {
                initialState[key] = parsedValue;
            }
        } catch (e) {
            // just skip invalid values
        }
    });

    const dashboardDates = initialState[filterIds.datesFilter];
    const today = startOfDay(new Date());
    if (
        !isValid(dashboardDates.from) ||
        !isValid(dashboardDates.to) ||
        startOfDay(dashboardDates.to) > today ||
        startOfDay(dashboardDates.from) > startOfDay(dashboardDates.to) ||
        differenceInCalendarDays(dashboardDates.to, dashboardDates.from) >= REPORTING_CALENDAR_MAX_DAYS
    ) {
        initialState[filterIds.datesFilter] = initDates();
    }

    const reportGranularities = getReportsGranularityValues(initialState[filterIds.datesFilter]);
    if (!includes(reportGranularities, initialState[filterIds.reportGranularityFilter])) {
        initialState[filterIds.reportGranularityFilter] = head(reportGranularities);
    }

    return initialState;
};

const buildCardsWithNewGranularity = (cards: any, dates: any, builder: (dates: any) => any[]) =>
    mapValues(cards, item => {
        if (item.granularity) {
            return {
                ...item,
                granularity: head(builder(dates))
            };
        }

        return item;
    });

export const filters = (state = getInitialState(), action: AnyAction): FiltersState => {
    const { type, payload } = action;
    switch (type) {
        case UPDATE_FILTER: {
            const { filterId } = action.payload;
            const nextState = { ...state };
            switch (filterId) {
                case filterIds.datesFilter: {
                    const dates = payload.value;
                    if (shouldUpdateReportGranularity(state[filterId], dates)) {
                        nextState.reportGranularityFilter = head(getReportsGranularityValues(dates));
                    }

                    if (shouldUpdateScorecardGranularity(state[filterId], dates)) {
                        nextState.cards = buildCardsWithNewGranularity(state.cards, dates, getDefaultGranularityValues);
                    }

                    if (shouldUpdateKeywordGranularity(state[filterId], dates)) {
                        nextState.cards = buildCardsWithNewGranularity(
                            state.cards,
                            dates,
                            getKeywordsGranularityValues
                        );
                    }

                    if (shouldUpdateSentimentGranularity(state[filterId], dates)) {
                        nextState.cards = buildCardsWithNewGranularity(
                            state.cards,
                            dates,
                            getSentimentGranularityValues
                        );
                    }
                    break;
                }

                case filterIds.reportViewFilter: {
                    const { value } = payload;
                    const viewValueChanged = nextState[payload.filterId].value !== value.value;
                    if (value.value !== reportViews.all && !viewValueChanged && value.subvalues.length === 0) {
                        return {
                            ...nextState,
                            [payload.filterId]: { value: '', subvalues: [] }
                        };
                    }
                    return {
                        ...nextState,
                        [payload.filterId]: { ...value },
                        [filterIds.reportTabGroupFilter]: viewValueChanged
                            ? mapViewAndGroupValues(value.value)
                            : nextState[filterIds.reportTabGroupFilter]
                    };
                }

                default:
                    break;
            }

            return {
                ...nextState,
                [payload.filterId]: isObject(payload.value) ? { ...payload.value } : payload.value
            };
        }
        case SET_REPORT_FILTERS_FROM_DASHBOARD: {
            return {
                ...state,
                [filterIds.reportAgentsFilter]: state[filterIds.agentsCommonDashboardFilter],
                [filterIds.reportTypeFilter]: payload.reportType,
                [filterIds.reportGranularityFilter]: get(
                    state,
                    `cards.${payload.cardId}.granularity`,
                    state[filterIds.reportGranularityFilter]
                ),
                ...setReportViewFilterFromDashboard(
                    state,
                    payload.cardId,
                    payload.user,
                    payload.dashboardSelections,
                    payload.entities
                )
            };
        }
        case SET_CARDS_FILTERS: {
            return {
                ...state,
                cards: { ...payload }
            };
        }
        case SET_CARD_FILTER: {
            const cards = state.cards ?? {};
            return {
                ...state,
                cards: {
                    ...cards,
                    [payload.cardId]: {
                        ...(cards[payload.cardId] ?? {}),
                        [payload.key]: payload.value
                    }
                }
            };
        }
        case SET_DATA_ROOM_MODE: {
            return {
                ...state,
                isDataRoomEnabled: payload.isDataRoomEnabled
            };
        }
        case SET_SELECTED_DASHBOARD_ITEMS: {
            const dashBoardSelections = {
                itemType: payload.itemType,
                itemsList: payload.itemsList
            };
            return {
                ...state,
                dashBoardSelections
            };
        }
        case LOGOUT: {
            return defaultState;
        }

        default: {
            return state;
        }
    }
};
